import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Table, Button, Space, Modal, Form, Input, Switch, message, Spin, Select, Alert } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons'
import UserDetailDrawer from './UserDetailDrawer'
import { useConfig } from '../../contexts/ConfigContext'
import { useCompany } from '../../contexts/CompanyContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus'
function UsersView() {
  const { config } = useConfig()
  const { securePost } = useSecureRequest()
  const { companies } = useCompany()
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [editingId, setEditingId] = useState<number | null>(null)
  const lastFetchedCompanyId = useRef<number | null>(null)

  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const [submitLoading, setSubmitLoading] = useState(false)

  const fetchUsers = useCallback(async (isBackground = false) => {
    if (!config.key || !companies.current?.id) return
    if (!isBackground) setLoading(true)
    try {
      const res = await securePost('/user-management/get-company-users-admin', {
        session: config.session,
        company_id: companies.current.id
      })
      if (res && Array.isArray(res.users)) {
        console.log('USERS:', res.users)
        setUsers(res.users)
      } else {
        setUsers([])
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar usuarios de esta empresa')
    } finally {
      if (!isBackground) setLoading(false)
    }
  }, [config.key, config.session, companies.current?.id])

  useEffect(() => {
    if (config.key && companies.current?.id && lastFetchedCompanyId.current !== companies.current.id) {
      lastFetchedCompanyId.current = companies.current.id
      fetchUsers()
    }
  }, [config.key, companies.current?.id, fetchUsers])

  useRefetchOnFocus(fetchUsers)

  const handleOpenCreate = () => {
    setEditingId(null)
    form.resetFields()
    form.setFieldsValue({
      company_id: companies.current?.id,
      is_active: true
    })
    setModalVisible(true)
  }

  const handleOpenEdit = (record: any) => {
    setEditingId(record.id)
    form.setFieldsValue({
      company_id: companies.current?.id, // Solo lectura en edición
      name: record.name,
      document_type: record.document_type,
      document_number: record.document_number,
      email: record.email,
      phone: record.phone,
      uid: record.uid,
      is_active: record.is_active
    })
    setModalVisible(true)
  }

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true)
    try {
      const { company_id, ...userData } = values
      let res;
      if (editingId) {
        res = await securePost('/user-management/update', {
          session: config.session,
          idUser: editingId,
          company_id: company_id || companies.current.id,
          user: userData
        })
        if (res?.message) throw new Error(res.message)
        message.success('Usuario actualizado exitosamente')
      } else {
        res = await securePost('/user-management/create', {
          session: config.session,
          company_id: company_id || companies.current.id,
          user: userData
        })
        if (res?.message) throw new Error(res.message)
        message.success('Usuario creado exitosamente')
      }
      setModalVisible(false)
      fetchUsers()
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al guardar usuario')
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Está seguro de desvincular a este usuario de la empresa?',
      okText: 'Desvincular',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await securePost('/user-management/delete', {
            session: config.session,
            idUser: id,
            company_id: companies.current.id
          })
          message.success('Usuario desvinculado exitosamente')
          fetchUsers()
        } catch (err) {
          console.error(err)
          message.error('Error al desvincular usuario')
        }
      }
    })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    { title: 'Documento', dataIndex: 'document_number', key: 'document_number' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'UID', dataIndex: 'uid', key: 'uid' },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (active ? 'Activo' : 'Inactivo')
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space wrap size="small">
          <Button 
            size="small"
            icon={<UserOutlined />} 
            onClick={() => {
              setSelectedUserId(record.id)
              setDrawerVisible(true)
            }}
          >
            Ver Detalle
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>Editar</Button>
          <Button size="small" type="primary" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Desvincular</Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Gestión de Usuarios - Empresa: {companies.current?.name || 'Cargando...'}</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Crear Usuario
        </Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
          <Spin />
        </div>
      ) : (
        <Table dataSource={users} columns={columns} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title={editingId ? 'Editar Usuario' : 'Crear Usuario'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editingId && (
            <Alert 
              message="Empresa Inicial" 
              description="Seleccione la empresa a la que se asociará este nuevo usuario." 
              type="info" 
              showIcon 
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item name="company_id" label="Empresa" rules={[{ required: true, message: 'Seleccione una empresa' }]}>
            <Select disabled={!!editingId} placeholder="Seleccione una empresa">
              {companies.list.map((c: any) => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese nombre' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="document_type" label="Tipo Documento" rules={[{ required: true, message: 'Ingrese tipo' }]}>
            <Input placeholder="Ej. CC, NIT" />
          </Form.Item>
          <Form.Item name="document_number" label="Número de Documento" rules={[{ required: true, message: 'Ingrese número' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Ingrese email válido' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <UserDetailDrawer
        userId={selectedUserId}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setSelectedUserId(null)
        }}
      />
    </div>
  )
}

export default UsersView
